import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { TFunction } from 'i18next';
import { matchRoute, ROUTER_ORDERS } from 'app/AppRouter';
import { trackAppEvent } from 'app/AppTracker';
import { getAscOrdersEnabled, getClaimsEnabled } from 'domains/appContext/AppContext.store';
import { getShowFirstHelp } from 'domains/firstHelp/FirstHelp.store';
import { getUserRights, UserRole } from 'domains/user';
import AscOrders from 'pages/MyOrdersPage/AscOrders';
import { InProgressOrders } from 'pages/MyOrdersPage/InProgressOrders/InProgressOrders';
import OrderClaims from 'pages/MyOrdersPage/OrderClaims';
import { Flex, MarginBox, Tab, TabItems, Tabs, Text } from 'UI';
import { getCondArrayItem, getData } from 'utils';
import { TRACKING_EVENT_ARCHIVED_ORDERS, TRACKING_EVENT_EXTERNAL_ORDERS } from 'utils/eventTracker/EventTracker.types';
import { useResetScrollSharp } from 'utils/hooks/useResetScroll';
import { ArchivedOrders } from './ArchivedOrders/ArchivedOrders';
import { ExternalOrders } from './ExternalOrders/ExternalOrders';
import { ARCHIVED, ASC_ORDERS, CLAIMS, EXTERNAL, IN_PROGRESS, ORDERS_TAB } from './Orders.type';

const getTabItems = (t: TFunction, isCdeIt: boolean, ascOrdersEnabled: boolean, claimsEnabled: boolean): TabItems[] => {
  return [
    {
      key: IN_PROGRESS,
      label: t('order.order_detail.in_progress', 'In progress'),
      component: <InProgressOrders />,
      'data-cy': 'orders-tab-in-progress',
    },
    ...getCondArrayItem(
      isCdeIt &&
        ascOrdersEnabled && {
          key: ASC_ORDERS,
          label: t('order.order_detail.asc_orders', 'My ASC orders'),
          component: <AscOrders />,
          'data-cy': 'orders-tab-my-asc-orders',
        },
    ),
    {
      key: EXTERNAL,
      label: t('order.order_detail.external', 'External orders'),
      additionalAction: () => trackAppEvent(TRACKING_EVENT_EXTERNAL_ORDERS),
      component: <ExternalOrders />,
      'data-cy': 'orders-tab-external-orders',
    },
    {
      key: ARCHIVED,
      label: t('order.order_detail.archived', 'Archived orders'),
      additionalAction: () => trackAppEvent(TRACKING_EVENT_ARCHIVED_ORDERS),
      component: <ArchivedOrders />,
      'data-cy': 'orders-tab-archived-orders',
    },
    ...getCondArrayItem(
      isCdeIt &&
        claimsEnabled && {
          key: CLAIMS,
          label: t('order.order_detail.claims', 'Claims'),
          component: <OrderClaims />,
          'data-cy': 'orders-tab-claims',
        },
    ),
  ];
};

const MyOrdersPage = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const userRoles = getData(useSelector(getUserRights)) ?? [];
  const isCdeIt = userRoles.find((x) => x === UserRole.R1_ORDER_AND_CLAIM);
  const ascOrdersEnabled = useSelector(getAscOrdersEnabled) ?? true;
  const claimsEnabled = useSelector(getClaimsEnabled) ?? true;
  const tabItems = getTabItems(t, !!isCdeIt, ascOrdersEnabled, claimsEnabled);
  const intialState = tabItems.find((item) => pathname.includes(item.key))?.key ?? IN_PROGRESS;
  const [tabState, setTabState] = useState<ORDERS_TAB>(intialState as ORDERS_TAB);
  const { showStreamId } = useSelector(getShowFirstHelp);

  const changeTabs = (activeKey: ORDERS_TAB) => {
    tabItems.find((item) => activeKey === item.key)?.additionalAction?.();
    setTabState(activeKey);
    if (history.location.pathname.startsWith(ROUTER_ORDERS)) {
      history.replace(`${ROUTER_ORDERS}/${activeKey}`);
    } else {
      history.push(`${ROUTER_ORDERS}/${activeKey}`);
    }
  };

  useEffect(() => {
    matchRoute(location.pathname, ROUTER_ORDERS, true) && changeTabs(IN_PROGRESS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  useEffect(() => {
    if (showStreamId) {
      setTabState(IN_PROGRESS);
    }
  }, [showStreamId]);

  useResetScrollSharp();

  return (
    <MarginBox mt={25}>
      <Flex direction={'column'}>
        <Text type={'h1_banner_light'} data-cy={'title-my-orders'}>
          {t('order.my_orders', 'My Orders')}
        </Text>
        <Tabs activeKey={tabState} onChange={changeTabs} tabBarGutter={15}>
          {tabItems?.map((item) => (
            <Tab tab={<span data-cy={item['data-cy']}>{item.label}</span>} key={item.key}>
              {item.component}
            </Tab>
          ))}
        </Tabs>
      </Flex>
    </MarginBox>
  );
};

export default MyOrdersPage;
